import { useState, useEffect, useMemo } from "react";
import { RateConfig } from "../sdk/types/data";
import { useSubscription } from "../context/SubscriptionContext";
import {
  MultiFeedFundingRateData,
  MultiFeedDeviationData,
  MultiFeedTWAPData,
} from "../sdk/types/api";

// Pure functions

/**
 * Get latest data from array or single value
 */
const getLatest = <T>(data: T | T[] | undefined): T | undefined => {
  if (!data) return undefined;
  return Array.isArray(data) ? data[0] : data;
};

/**
 * Create subscription callback key
 */
/**
 * Hook for managing rate data (funding_rate, deviation, twap) with WebSocket real-time updates
 * Automatically subscribes to all rate sub-channels for a single market ID
 */
export function useRates(config: RateConfig) {
  const historyDepth = config.historyDepth ?? 10;
  const marketId = config.marketId;

  // State for each rate type (single market, so we store directly or as array for history)
  const [fundingRate, setFundingRate] = useState<
    MultiFeedFundingRateData | MultiFeedFundingRateData[] | undefined
  >(undefined);
  const [deviation, setDeviation] = useState<
    MultiFeedDeviationData | MultiFeedDeviationData[] | undefined
  >(undefined);
  const [twap, setTwap] = useState<
    MultiFeedTWAPData | MultiFeedTWAPData[] | undefined
  >(undefined);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const { subscribe: sub, unsubscribe: unsub, isConnected: contextConnected } = useSubscription();

  useEffect(() => {
    setLoading(false);
    setIsConnected(contextConnected);
    if (!marketId) return;

    setFundingRate(undefined);
    setDeviation(undefined);
    setTwap(undefined);
    setError(null);

    const onFundingRate = (data: unknown) => {
      setFundingRate((prev) =>
        historyDepth === 1
          ? (data as MultiFeedFundingRateData)
          : [data as MultiFeedFundingRateData, ...(Array.isArray(prev) ? prev : prev ? [prev] : [])].slice(0, historyDepth) as MultiFeedFundingRateData | MultiFeedFundingRateData[],
      );
    };
    const onDeviation = (data: unknown) => {
      setDeviation((prev) =>
        historyDepth === 1
          ? (data as MultiFeedDeviationData)
          : [data as MultiFeedDeviationData, ...(Array.isArray(prev) ? prev : prev ? [prev] : [])].slice(0, historyDepth) as MultiFeedDeviationData | MultiFeedDeviationData[],
      );
    };
    const onTwap = (data: unknown) => {
      setTwap((prev) =>
        historyDepth === 1
          ? (data as MultiFeedTWAPData)
          : [data as MultiFeedTWAPData, ...(Array.isArray(prev) ? prev : prev ? [prev] : [])].slice(0, historyDepth) as MultiFeedTWAPData | MultiFeedTWAPData[],
      );
    };

    const k1 = `funding_rate:${marketId}`;
    const k2 = `deviation:${marketId}`;
    const k3 = `twap:${marketId}`;
    sub(k1, onFundingRate);
    sub(k2, onDeviation);
    sub(k3, onTwap);
    return () => {
      unsub(k1, onFundingRate);
      unsub(k2, onDeviation);
      unsub(k3, onTwap);
    };
  }, [marketId, historyDepth, contextConnected, sub, unsub]);

  // Get latest data (helper for single market)
  const latestFundingRate = useMemo(
    () => getLatest(fundingRate),
    [fundingRate],
  );

  const latestDeviation = useMemo(() => getLatest(deviation), [deviation]);

  const latestTWAP = useMemo(() => getLatest(twap), [twap]);

  return {
    fundingRate,
    deviation,
    twap,
    latestFundingRate,
    latestDeviation,
    latestTWAP,
    loading,
    error,
    isConnected,
  };
}
